import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Box, Briefcase, CalendarDays, CheckSquare, DollarSign,
  Coins, Crown, FileSignature, Gamepad2, Gem, Gift, RefreshCw, Rocket,
  Settings2, ShieldCheck, ShoppingBag, Sparkles, Swords, Target, Trophy, Users, Zap
} from 'lucide-react'
import { motion } from 'motion/react'
import toast from 'react-hot-toast'
import AchievementCard from '../components/AchievementCard'
import AchievementModal from '../components/AchievementModal'
import AchievementShowcaseEditor from '../components/AchievementShowcaseEditor'
import { useAchievementsStore } from '../store/useAchievementsStore'
import useAppStore from '../../../store/useAppStore'
import { normalizeLanguageCode } from '../../../i18n/config'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'

const CATEGORY_ICONS = {
  cases: Box,
  crash: Rocket,
  double: Coins,
  wheel50: Target,
  upgrader: Zap,
  contract: FileSignature,
  casebattle: Swords,
  classic: Crown,
  market: ShoppingBag,
  luck: Sparkles,
  deposit: Gem,
  withdraw: DollarSign,
  events: Gift,
  social: Users,
  loyalty: CalendarDays,
  tasks: CheckSquare,
  collection: Briefcase
}

const SECTIONS_VARIANT = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }
const SECTION_VARIANT = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
}

const AchievementsSkeleton = () => (
  <div className="mx-auto w-full max-w-5xl space-y-5 p-4 lg:p-8">
    <div className="h-36 animate-pulse rounded-2xl border border-white/10 bg-surface" />
    {[0, 1, 2].map((section) => (
      <div key={section} className="space-y-3">
        <div className="h-5 w-36 animate-pulse rounded bg-white/10" />
        <div className="grid gap-3 md:grid-cols-2">
          <div className="h-28 animate-pulse rounded-2xl bg-surface" />
          <div className="h-28 animate-pulse rounded-2xl bg-surface" />
        </div>
      </div>
    ))}
  </div>
)

const Achievements = () => {
  const { slug } = useParams()
  const isGuest = useAppStore((state) => state.isGuest)
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { achievements, isLoading, isSaving, error, loadAchievements, saveShowcase, reset } = useAchievementsStore()
  const [selectedAchievement, setSelectedAchievement] = useState(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const language = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || 'en'

  useEffect(() => {
    loadAchievements(slug, language)
    return () => reset()
  }, [language, loadAchievements, reset, slug, isGuest])

  const allAchievements = useMemo(() => (
    achievements?.categories?.flatMap((category) => category.achievements || []) || []
  ), [achievements])

  const handleBack = () => {
    hapticImpact('light')
    if (window.history.length > 1) navigate(-1)
    else navigate(slug ? `/profile/${slug}` : '/profile')
  }

  const saveCodes = async (codes) => {
    const result = await saveShowcase(codes)
    if (!result?.ok) {
      hapticNotify('error')
      toast.error(t('achievements.saveError'))
      return
    }
    hapticNotify('success')
    toast.success(t(codes.length ? 'achievements.saved' : 'achievements.resetDone'))
    setIsEditorOpen(false)
  }

  if (isLoading) return <AchievementsSkeleton />

  if (error || !achievements) {
    return (
      <div className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center justify-center p-4 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-surface">
          <RefreshCw size={34} className="text-text-secondary" />
        </div>
        <h1 className="mt-4 text-xl font-black text-white">
          {error === 'rate_limited' ? t('api.rateLimited') : error === 'not_found' ? t('achievements.notFound') : t('achievements.loadError')}
        </h1>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={handleBack} className="rounded-xl border border-white/10 px-5 py-3 font-semibold text-white active:scale-[0.98]">{t('common.back')}</button>
          {error !== 'not_found' && (
            <button type="button" onClick={() => loadAchievements(slug, language)} className="rounded-xl bg-accent-primary px-5 py-3 font-bold text-dark-primary active:scale-[0.98]">{t('achievements.retry')}</button>
          )}
        </div>
      </div>
    )
  }

  return (
    <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="mx-auto w-full max-w-5xl p-4 lg:p-8">
      <motion.div variants={SECTION_VARIANT} className="mb-4 flex items-center justify-between">
        <button type="button" onClick={handleBack} className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-surface text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] active:scale-[0.98]" aria-label={t('common.back')}>
          <ArrowLeft size={20} />
        </button>
        {achievements.is_owner && (
          <button type="button" onClick={() => setIsEditorOpen(true)} className="flex items-center gap-2 rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-3 py-2.5 text-sm font-semibold text-accent-primary active:scale-[0.98]">
            <Settings2 size={16} />
            {t('achievements.customize')}
          </button>
        )}
      </motion.div>

      <motion.section variants={SECTION_VARIANT} className="relative mb-7 overflow-hidden rounded-2xl border border-white/10 bg-surface p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_18px_30px_-16px_rgba(0,0,0,0.6)] lg:p-7">
        <div className="pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full bg-amber-400/10 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-amber-400/30 bg-amber-400/10 text-amber-300 shadow-[0_0_25px_rgba(251,191,36,0.12)]">
            <Trophy size={27} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-300">{t('achievements.collection')}</p>
            <h1 className="hidden mt-1 text-2xl font-black text-white lg:text-3xl">{t('achievements.title')}</h1>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black tabular-nums text-white lg:text-3xl">
              {achievements.earned_count}<span className="text-text-secondary">/{achievements.total_count}</span>
            </div>
            <div className="text-[10px] uppercase tracking-wide text-text-secondary">{t('achievements.unlocked')}</div>
          </div>
        </div>
        <div className="relative mt-5 h-2 overflow-hidden rounded-full bg-white/5">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-accent-primary" style={{ width: `${achievements.total_count ? Math.min(achievements.earned_count / achievements.total_count, 1) * 100 : 0}%` }} />
        </div>
        {achievements.is_owner && (
          <div className="hidden relative mt-3 flex items-center gap-2 text-xs text-text-secondary">
            <ShieldCheck size={14} className="text-accent-primary" />
            {achievements.showcase_custom ? t('achievements.showcaseCustom') : t('achievements.showcaseAuto')}
          </div>
        )}
      </motion.section>

      <div className="space-y-7">
        {achievements.categories.map((category) => {
          const Icon = CATEGORY_ICONS[category.slug] || Gamepad2
          return (
            <motion.section key={category.slug} variants={SECTION_VARIANT}>
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-info">
                  <Icon size={16} />
                </span>
                <h2 className="text-base font-black not-italic uppercase tracking-wide text-white">{category.name}</h2>
                <span className="hidden ml-auto rounded-full bg-white/5 px-2 py-1 text-[10px] tabular-nums text-text-secondary">{category.achievements.length}</span>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {category.achievements.map((achievement) => (
                  <AchievementCard key={achievement.code} achievement={achievement} language={language} onClick={setSelectedAchievement} />
                ))}
              </div>
            </motion.section>
          )
        })}
      </div>

      {achievements.categories.length === 0 && (
        <motion.div variants={SECTION_VARIANT} className="rounded-2xl border border-white/10 bg-surface p-10 text-center">
          <Trophy size={36} className="mx-auto text-text-secondary" />
          <p className="mt-3 text-sm text-text-secondary">{t('achievements.empty')}</p>
        </motion.div>
      )}

      <AchievementModal achievement={selectedAchievement} onClose={() => setSelectedAchievement(null)} language={language} />
      <AchievementShowcaseEditor
        isOpen={isEditorOpen}
        achievements={allAchievements}
        initialCodes={achievements.showcase || []}
        isSaving={isSaving}
        onClose={() => setIsEditorOpen(false)}
        onSave={saveCodes}
        onReset={() => saveCodes([])}
      />
    </motion.div>
  )
}

export default Achievements
